// src/components/Admin/ManageMatches.jsx
import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

export default function ManageMatches() {
  const [matches, setMatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [placements, setPlacements] = useState({});
  const [form, setForm] = useState({
    event_id: '',
    round: '',
    team1_id: '',
    team2_id: '',
  });
  const [message, setMessage] = useState('');

  const selectedEvent = useMemo(
    () => events.find(e => String(e.id) === String(selectedEventId)),
    [events, selectedEventId]
  );

  const isSport = !!selectedEvent?.is_sport;

  useEffect(() => {
    fetchMatches();
    fetchEvents();
    fetchColleges();
  }, []);

  useEffect(() => {
    setForm(f => ({ ...f, event_id: selectedEventId || '' }));
    if (selectedEventId && selectedEvent && !selectedEvent.is_sport) {
      fetchEventResults(selectedEventId);
    }
  }, [selectedEvent, selectedEventId, selectedEvent?.is_sport]);

  const fetchMatches = async () => {
    try {
      const res = await api.get('/matches');
      setMatches(res.data);
      setMessage('');
    } catch (err) {
      setMessage('❌ Failed to load matches');
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await api.get('/events');
      setEvents(res.data);
    } catch (err) {
      setMessage('❌ Failed to load events');
    }
  };

  const fetchColleges = async () => {
    try {
      const res = await api.get('/colleges');
      setColleges(res.data);
    } catch (err) {
      setMessage('❌ Failed to load colleges');
    }
  };

  // ---------- Non-sports ----------
  const fetchEventResults = async (eventId) => {
    try {
      const res = await api.get(`/eventresults/${eventId}`);
      const map = {};
      res.data.forEach(r => {
        map[r.college_id] = r.placement ?? '';
      });
      setPlacements(map);
    } catch (err) {
      setMessage('❌ Failed to load placements');
    }
  };

  const savePlacement = async (collegeId, placementVal) => {
    try {
      const placement = placementVal === '' ? null : Number(placementVal);
      await api.post('/eventresults', {
        event_id: Number(selectedEventId),
        college_id: Number(collegeId),
        placement,
      });
      setPlacements(prev => ({ ...prev, [collegeId]: placementVal }));
      setMessage('✅ Placement saved!');
    } catch (err) {
      setMessage('❌ Failed to save placement: ' + (err.response?.data?.error || err.message));
    }
  };

  const usedPlacements = useMemo(() => {
    const used = new Set();
    Object.values(placements).forEach(p => {
      const n = Number(p);
      if ([1, 2, 3, 4, 5].includes(n)) used.add(n);
    });
    return used;
  }, [placements]);

  // ---------- Sports ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/matches', form);
      setForm({ event_id: selectedEventId || '', round: '', team1_id: '', team2_id: '' });
      fetchMatches();
      setMessage('✅ Match created successfully!');
    } catch (err) {
      setMessage('❌ Failed to create match: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleWinner = async (matchId, winnerId) => {
    try {
      await api.put(`/matches/${matchId}`, { winner_id: winnerId, status: 'completed' });
      fetchMatches();
      setMessage('🏆 Winner updated successfully!');
    } catch (err) {
      setMessage('❌ Failed to update winner: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this match?')) return;
    try {
      await api.delete(`/matches/${matchId}`);
      fetchMatches();
      setMessage('🗑️ Match deleted successfully!');
    } catch (err) {
      setMessage('❌ Failed to delete match: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateScores = async (matchId) => {
    const scores = prompt('Enter scores (team1_score,team2_score):');
    if (!scores) return;
    const [team1_score, team2_score] = scores.split(',').map(Number);
    try {
      await api.put(`/matches/${matchId}/scores`, { team1_score, team2_score });
      fetchMatches();
      setMessage('✅ Scores updated successfully!');
    } catch (err) {
      setMessage('❌ Failed to update scores: ' + (err.response?.data?.error || err.message));
    }
  };

  const visibleMatches = useMemo(() => {
    if (!selectedEventId) return matches;
    return matches.filter(m => String(m.event_id) === String(selectedEventId));
  }, [matches, selectedEventId]);

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">Manage Matches & Brackets</h2>

      {message && (
        <div
          className={`p-3 mb-4 rounded text-white ${
            message.startsWith('✅') || message.startsWith('🏆') || message.startsWith('🗑️')
              ? 'bg-green-600'
              : 'bg-red-600'
          }`}
        >
          {message}
        </div>
      )}

      {/* Event selector */}
      <div className="bg-gray-50 p-4 rounded mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <select
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          className="p-2 border rounded"
        >
          <option value="">Choose Event (to manage)</option>
          {events.map(e => (
            <option key={e.id} value={e.id}>
              {e.name} ({e.gender}) {e.is_sport ? '• Sport' : '• Non-sport'}
            </option>
          ))}
        </select>

        {selectedEvent && (
          <div className="md:col-span-3 flex items-center text-sm text-gray-600">
            <span className="mr-3">
              Mode: <strong>{isSport ? 'Sports (Match-based)' : 'Non-sports (Placements)'}</strong>
            </span>
            {!isSport && <span>Assign Champion / 1st / 2nd / 3rd / 4th to all colleges.</span>}
          </div>
        )}
      </div>

      {/* ---------- SPORTS ---------- */}
      {(!selectedEvent || isSport) && (
        <>
          {/* Create Match */}
          <form
            onSubmit={handleSubmit}
            className="bg-gray-50 p-4 rounded mb-6 grid grid-cols-1 md:grid-cols-5 gap-3"
          >
            <select
              value={form.event_id}
              onChange={(e) => {
                setForm({ ...form, event_id: e.target.value });
                setSelectedEventId(e.target.value);
              }}
              className="p-2 border rounded"
              required
            >
              <option value="">Event</option>
              {events.map(e => (
                <option key={e.id} value={e.id}>
                  {e.name} ({e.gender})
                </option>
              ))}
            </select>

            {/* ✅ Round dropdown */}
            <select
              value={form.round}
              onChange={(e) => setForm({ ...form, round: e.target.value })}
              className="p-2 border rounded"
              required
            >
              <option value="">Select Round</option>
              <option value="Elimination">Elimination</option>
              <option value="Quarterfinal">Quarterfinal</option>
              <option value="Semifinal">Semifinal</option>
              <option value="Final">Final</option>
              <option value="Championship">Championship</option>
            </select>

            <select
              value={form.team1_id}
              onChange={(e) => setForm({ ...form, team1_id: e.target.value })}
              className="p-2 border rounded"
              required
            >
              <option value="">Team 1</option>
              {colleges.map(c => (
                <option key={c.id} value={c.id}>{c.acronym}</option>
              ))}
            </select>

            <select
              value={form.team2_id}
              onChange={(e) => setForm({ ...form, team2_id: e.target.value })}
              className="p-2 border rounded"
              required
            >
              <option value="">Team 2</option>
              {colleges.map(c => (
                <option key={c.id} value={c.id}>{c.acronym}</option>
              ))}
            </select>

            <button type="submit" className="btn btn-primary">Add</button>
          </form>

          {/* Matches List */}
          <div className="space-y-4">
            {visibleMatches.map(m => {
              const team1 = colleges.find(c => c.id === m.team1_id);
              const team2 = colleges.find(c => c.id === m.team2_id);
              const winner = colleges.find(c => c.id === m.winner_id);
              const eventName = events.find(e => e.id === m.event_id)?.name;

              return (
                <div key={m.id} className="p-4 bg-white border rounded shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <strong>
                      {m.round} • {eventName}
                      {['championship', 'final'].includes(m.round?.toLowerCase()) && (
                        <span className="ml-2 text-yellow-600 font-semibold">🏆</span>
                      )}
                    </strong>
                    {winner && <span className="text-green-600 font-bold">🏆 {winner.acronym}</span>}
                  </div>

                  <div className="flex flex-wrap gap-3 items-center">
                    <span>{team1?.acronym || 'TBD'} ({m.team1_score || '-'})</span>
                    <span className="text-gray-500">vs</span>
                    <span>{team2?.acronym || 'TBD'} ({m.team2_score || '-'})</span>

                    <select
                      onChange={(e) => handleWinner(m.id, e.target.value)}
                      defaultValue={m.winner_id || ''}
                      disabled={!!m.winner_id}
                      className={`ml-2 p-1 border rounded text-sm ${m.winner_id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Select Winner</option>
                      <option value={m.team1_id}>{team1?.acronym}</option>
                      <option value={m.team2_id}>{team2?.acronym}</option>
                    </select>

                    <button onClick={() => handleUpdateScores(m.id)} className="ml-2 p-1 border rounded text-sm">
                      Update Scores
                    </button>

                    <button onClick={() => handleDelete(m.id)} className="text-red-500 hover:text-red-700 text-sm ml-2">
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}

            {visibleMatches.length === 0 && (
              <div className="text-sm text-gray-500">No matches to show for this selection.</div>
            )}
          </div>
        </>
      )}

      {/* ---------- NON-SPORTS ---------- */}
      {selectedEvent && !isSport && (
        <div className="bg-white border rounded shadow-sm p-4">
          <h3 className="font-bold mb-4">{selectedEvent.name} • Assign Placements</h3>
          <div className="overflow-x-auto">
            <table className="w-full border">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border p-2 text-left">College</th>
                  <th className="border p-2 text-left">Placement</th>
                </tr>
              </thead>
              <tbody>
                {colleges.map(c => {
                  const val = placements[c.id] ?? '';
                  return (
                    <tr key={c.id}>
                      <td className="border p-2">{c.acronym}</td>
                      <td className="border p-2">
                        <select
                          value={val}
                          onChange={(e) => savePlacement(c.id, e.target.value)}
                          className="p-1 border rounded"
                        >
                          <option value="">No Rank</option>
                          <option value="1" disabled={val !== '1' && usedPlacements.has(1)}>Champion</option>
                          <option value="2" disabled={val !== '2' && usedPlacements.has(2)}>1st Place</option>
                          <option value="3" disabled={val !== '3' && usedPlacements.has(3)}>2nd Place</option>
                          <option value="4" disabled={val !== '4' && usedPlacements.has(4)}>3rd Place</option>
                          <option value="5" disabled={val !== '5' && usedPlacements.has(5)}>4th Place</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm">
            <div className="font-semibold mb-1">Current podium:</div>
            <ul className="list-disc pl-5">
              <li>Champion: {colleges.find(c => String(placements[c.id]) === '1')?.acronym || '-'}</li>
              <li>1st Place: {colleges.find(c => String(placements[c.id]) === '2')?.acronym || '-'}</li>
              <li>2nd Place: {colleges.find(c => String(placements[c.id]) === '3')?.acronym || '-'}</li>
              <li>3rd Place: {colleges.find(c => String(placements[c.id]) === '4')?.acronym || '-'}</li>
              <li>4th Place: {colleges.find(c => String(placements[c.id]) === '5')?.acronym || '-'}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
